import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Providers/AuthProvider';

const ROOSTER_TEMPLATE_ID = 'd5d0d5d0-d5d0-d5d0-d5d0-d5d0d5d0d5d0';

// Componente Drag & Drop Reutilizable para subida de imágenes
const FileDropzone = ({ label, value, onUpload, onDelete, type = 'element', fieldName, uploadingField, helpText }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const isUploading = uploadingField === fieldName;

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            onUpload(file, fieldName, type);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            onUpload(file, fieldName, type);
        }
    };

    const getPreviewUrl = (val) => {
        if (!val) return '';
        if (val.startsWith('http://') || val.startsWith('https://')) return val;
        const base = API_URL.replace(/\/api\/?$/, '');
        return `${base}${val.startsWith('/') ? '' : '/'}${val}`;
    };

    return (
        <div className="mb-3">
            {label && <label className="form-label fw-bold small mb-1">{label}</label>}
            {helpText && <span className="d-block text-muted small mb-1">{helpText}</span>}
            <div 
                className={`border rounded p-3 text-center position-relative d-flex flex-column align-items-center justify-content-center ${isDragOver ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle bg-light'}`}
                style={{ 
                    borderStyle: 'dashed', 
                    borderWidth: '2px', 
                    minHeight: '115px',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`file-${fieldName}`).click()}
            >
                <input 
                    type="file" 
                    id={`file-${fieldName}`} 
                    className="d-none" 
                    accept="image/*"
                    onChange={handleFileChange} 
                />
                
                {isUploading ? (
                    <div className="d-flex flex-column align-items-center">
                        <div className="spinner-border spinner-border-sm text-primary mb-1" role="status"></div>
                        <span className="small text-muted">Subiendo imagen...</span>
                    </div>
                ) : value ? (
                    <div className="position-relative d-flex flex-column align-items-center w-100" onClick={(e) => e.stopPropagation()}>
                        <img 
                            src={getPreviewUrl(value)} 
                            alt="Preview" 
                            className="img-thumbnail mb-1 shadow-sm" 
                            style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain' }} 
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div className="d-flex gap-2 align-items-center">
                            <span className="small text-truncate text-muted d-block" style={{ maxWidth: '160px' }}>
                                {value.split('/').pop()}
                            </span>
                            <button 
                                type="button" 
                                className="btn btn-sm btn-outline-danger border-0 p-0 px-1"
                                title="Eliminar imagen"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(fieldName);
                                }}
                            >
                                <i className="bi bi-trash3-fill"></i>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="d-flex flex-column align-items-center text-secondary small">
                        <i className="bi bi-cloud-arrow-up-fill fs-3 text-warning mb-1"></i>
                        <span>Arrastra o haz clic para subir</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const initialFormData = {
    themeName: '',
    sponsorLogo: '',
    screenSponsor: '',
    fanBackground: '',
    characterNormal: '',
    characterHit: '',
    backObstacle: '',
    frontObstacle: '',
    bonusStage1: '',
    bonusStage2: '',
    bonusStage3: '',
    screenBackground: '',
    overlayBanner1536: '',
    overlayBanner1920: ''
};

const RoosterThemeModal = ({ show, onClose, onSave, roomId = null, onApply = null }) => {
    const { authenticatedFetch } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [activeTab, setActiveTab] = useState('general'); // general, fan, bonus, screen
    const [uploadingField, setUploadingField] = useState(null);
    const [formData, setFormData] = useState(initialFormData);

    const loadConfigs = async () => {
        setLoading(true);
        try {
            const res = await authenticatedFetch(`${API_URL}v2/gameconfigs/template/${ROOSTER_TEMPLATE_ID}`);
            if (res.ok) {
                const data = await res.json();
                setConfigs(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error("Error cargando temas de PluckingRooster:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            loadConfigs();
            handleNewConfig();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const handleSelectConfig = (config) => {
        setSelectedConfig(config);
        setIsEditing(true);
        setActiveTab('general');

        let datos = {};
        if (typeof config.datosDinamica === 'string') {
            try { datos = JSON.parse(config.datosDinamica); } catch(e) {}
        } else {
            datos = config.datosDinamica || {};
        }

        setFormData({
            themeName: config.nombreConfiguracion || '',
            sponsorLogo: datos.sponsorLogo || '',
            screenSponsor: datos.screenSponsor || '',
            fanBackground: datos.fanBackground || '',
            characterNormal: datos.characterNormal || '',
            characterHit: datos.characterHit || '',
            backObstacle: datos.backObstacle || '',
            frontObstacle: datos.frontObstacle || '',
            bonusStage1: datos.bonusStage1 || '',
            bonusStage2: datos.bonusStage2 || '',
            bonusStage3: datos.bonusStage3 || '',
            screenBackground: datos.screenBackground || '',
            overlayBanner1536: datos.overlayBanner1536 || '',
            overlayBanner1920: datos.overlayBanner1920 || ''
        });
    };

    const handleNewConfig = () => {
        setSelectedConfig(null);
        setIsEditing(false);
        setActiveTab('general');
        setFormData(initialFormData);
    };

    const handleStartCreate = () => {
        setSelectedConfig(null);
        setIsEditing(true);
        setActiveTab('general');
        setFormData(initialFormData);
    };

    const handleUploadImage = async (fileOrEvent, fieldName, type = 'element') => {
        let file;
        if (fileOrEvent && fileOrEvent.target && fileOrEvent.target.files) {
            file = fileOrEvent.target.files[0];
        } else {
            file = fileOrEvent;
        }
        if (!file) return;

        setUploadingField(fieldName);
        const data = new FormData();
        data.append('file', file);

        try {
            const token = localStorage.getItem('interaktia_token');
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await fetch(`${API_URL}PluckingTheRooster/upload-asset?type=${type}`, {
                method: 'POST',
                headers: headers,
                body: data
            });

            if (res.ok) {
                const resJson = await res.json();
                const uploadedUrl = resJson.url;
                setFormData(prev => ({ ...prev, [fieldName]: uploadedUrl }));
            } else {
                alert("Error al subir la imagen al servidor.");
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            alert("Error de conexión al subir la imagen.");
        } finally {
            setUploadingField(null);
        }
    };

    const handleDeleteImage = (fieldName) => {
        setFormData(prev => ({ ...prev, [fieldName]: '' }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.themeName.trim()) {
            alert("El nombre del tema es obligatorio.");
            return;
        }

        const dataPayload = {
            themeName: formData.themeName.trim(),
            fanBackground: formData.fanBackground,
            sponsorLogo: formData.sponsorLogo,
            characterNormal: formData.characterNormal,
            characterHit: formData.characterHit,
            backObstacle: formData.backObstacle,
            frontObstacle: formData.frontObstacle,
            bonusStage1: formData.bonusStage1,
            bonusStage2: formData.bonusStage2,
            bonusStage3: formData.bonusStage3,
            screenBackground: formData.screenBackground,
            screenSponsor: formData.screenSponsor,
            overlayBanner1536: formData.overlayBanner1536,
            overlayBanner1920: formData.overlayBanner1920
        };

        const configBody = {
            templateId: ROOSTER_TEMPLATE_ID,
            nombreConfiguracion: formData.themeName.trim(),
            datosDinamica: dataPayload
        };

        setLoading(true);
        try {
            let res;
            if (selectedConfig && selectedConfig.id) {
                // Actualizar tema existente
                res = await authenticatedFetch(`${API_URL}v2/gameconfigs/${selectedConfig.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(configBody)
                });
            } else {
                // Crear nuevo tema
                res = await authenticatedFetch(`${API_URL}v2/gameconfigs`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(configBody)
                });
            }

            if (res.ok) {
                alert("¡Tema guardado exitosamente!");
                setIsEditing(false);
                loadConfigs();
                if (onSave) onSave();
            } else {
                const errText = await res.text();
                alert(`Error al guardar tema: ${errText}`);
            }
        } catch (error) {
            console.error("Error saving rooster theme:", error);
            alert("Error de conexión al guardar el tema.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar permanentemente este tema?")) return;

        setLoading(true);
        try {
            const res = await authenticatedFetch(`${API_URL}v2/gameconfigs/${id}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                alert("Tema eliminado exitosamente.");
                loadConfigs();
                if (onSave) onSave();
            } else {
                alert("No fue posible eliminar el tema.");
            }
        } catch (error) {
            console.error("Error deleting theme:", error);
            alert("Error de red al eliminar el tema.");
        } finally {
            setLoading(false);
        }
    };

    const handleApplyToRoom = async (config) => {
        if (!roomId) {
            alert("No hay una sala activa seleccionada para aplicar este tema.");
            return;
        }

        if (!window.confirm(`¿Deseas aplicar el tema "${config.nombreConfiguracion}" a la sala actual?`)) return;

        setLoading(true);
        try {
            const bRes = await authenticatedFetch(`${API_URL}v2/rooms/${roomId}/bootstrap`);
            if (bRes.ok) {
                const bData = await bRes.json();
                const updateRes = await authenticatedFetch(`${API_URL}v2/rooms/${roomId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        nombreSala: bData.nombreSala || 'Sala',
                        gameTemplateId: ROOSTER_TEMPLATE_ID,
                        configId: config.id,
                        isDefault: bData.isDefault ?? false,
                        zoneListId: bData.zoneListId
                    })
                });

                if (updateRes.ok) {
                    alert(`¡El tema "${config.nombreConfiguracion}" se aplicó exitosamente a la sala!`);
                    if (onApply) onApply(config);
                    if (onSave) onSave();
                } else {
                    const err = await updateRes.text();
                    alert(`No se pudo aplicar el tema a la sala: ${err}`);
                }
            } else {
                alert("No fue posible recuperar los datos de la sala.");
            }
        } catch (err) {
            console.error("Error aplicando tema a la sala:", err);
            alert("Error de red al aplicar el tema a la sala.");
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1060, overflowY: 'auto' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '15px' }}>
                    <div className="modal-header bg-dark text-white border-0 py-3">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-palette2 text-warning fs-4"></i>
                            <div>
                                <h5 className="modal-title fw-bold mb-0">Personalización de Temas: Pégale al Personaje / Gallo</h5>
                                <small className="text-muted">Motor de Dinámicas Visuales Interaktia V2</small>
                            </div>
                        </div>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
                    </div>

                    <div className="modal-body p-4">
                        {!isEditing ? (
                            /* VISTA LISTADO DE TEMAS */
                            <div>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <h6 className="fw-bold mb-0 text-dark">Biblioteca de Temas Guardados</h6>
                                        <small className="text-muted">Selecciona un tema para editarlo, eliminarlo o aplicarlo a la sala activa.</small>
                                    </div>
                                    <button className="btn btn-warning btn-sm fw-bold px-3 shadow-sm text-dark" onClick={handleStartCreate}>
                                        <i className="bi bi-plus-lg me-1"></i> Crear Nuevo Tema
                                    </button>
                                </div>

                                {loading && (
                                    <div className="text-center my-4">
                                        <div className="spinner-border text-warning" role="status"></div>
                                        <p className="small text-muted mt-2">Cargando biblioteca de temas...</p>
                                    </div>
                                )}

                                {!loading && configs.length === 0 && (
                                    <div className="alert alert-light text-center py-5 border rounded-3">
                                        <i className="bi bi-stars text-warning fs-1 d-block mb-2"></i>
                                        <p className="fw-bold mb-1">Aún no existen temas personalizados para esta dinámica</p>
                                        <span className="text-muted small">Haz clic en "Crear Nuevo Tema" para personalizar personajes, fondos y patrocinadores.</span>
                                    </div>
                                )}

                                <div className="list-group shadow-sm">
                                    {configs.map(cfg => (
                                        <div key={cfg.id} className="list-group-item list-group-item-action d-flex flex-wrap justify-content-between align-items-center py-3">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="bg-warning-subtle text-warning-emphasis p-2 rounded-3">
                                                    <i className="bi bi-controller fs-4"></i>
                                                </div>
                                                <div>
                                                    <h6 className="fw-bold mb-0 text-dark">{cfg.nombreConfiguracion}</h6>
                                                    <small className="text-muted">Template ID: {cfg.templateId}</small>
                                                </div>
                                            </div>

                                            <div className="d-flex gap-2 align-items-center mt-2 mt-md-0">
                                                {roomId && (
                                                    <button 
                                                        className="btn btn-outline-success btn-sm fw-bold shadow-sm"
                                                        title="Asignar tema a la sala actual"
                                                        onClick={() => handleApplyToRoom(cfg)}
                                                    >
                                                        <i className="bi bi-check2-circle me-1"></i> Aplicar a esta Sala
                                                    </button>
                                                )}
                                                <button 
                                                    className="btn btn-outline-secondary btn-sm"
                                                    title="Editar configuración"
                                                    onClick={() => handleSelectConfig(cfg)}
                                                >
                                                    <i className="bi bi-pencil-fill me-1"></i> Editar
                                                </button>
                                                <button 
                                                    className="btn btn-outline-danger btn-sm"
                                                    title="Eliminar tema"
                                                    onClick={() => handleDelete(cfg.id)}
                                                >
                                                    <i className="bi bi-trash-fill"></i>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            /* FORMULARIO DE EDICIÓN CON PESTAÑAS BOOTSTRAP 5 */
                            <form onSubmit={handleSave}>
                                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-secondary btn-sm fw-semibold" 
                                        onClick={() => setIsEditing(false)}
                                    >
                                        <i className="bi bi-arrow-left me-1"></i> Volver a la Biblioteca
                                    </button>
                                    <span className="badge bg-dark px-3 py-2 fs-6">
                                        {selectedConfig ? `Editando: ${selectedConfig.nombreConfiguracion}` : 'Creando Nuevo Tema'}
                                    </span>
                                </div>

                                {/* NAVEGACIÓN POR PESTAÑAS */}
                                <ul className="nav nav-pills nav-fill mb-4 bg-light p-2 rounded-3 border">
                                    <li className="nav-item">
                                        <button 
                                            type="button" 
                                            className={`nav-link fw-bold ${activeTab === 'general' ? 'active bg-warning text-dark shadow-sm' : 'text-secondary'}`}
                                            onClick={() => setActiveTab('general')}
                                        >
                                            <i className="bi bi-tag-fill me-1"></i> 1. General & Patrocinio
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button 
                                            type="button" 
                                            className={`nav-link fw-bold ${activeTab === 'fan' ? 'active bg-warning text-dark shadow-sm' : 'text-secondary'}`}
                                            onClick={() => setActiveTab('fan')}
                                        >
                                            <i className="bi bi-phone-fill me-1"></i> 2. Móvil Aficionado & Hoyos
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button 
                                            type="button" 
                                            className={`nav-link fw-bold ${activeTab === 'bonus' ? 'active bg-warning text-dark shadow-sm' : 'text-secondary'}`}
                                            onClick={() => setActiveTab('bonus')}
                                        >
                                            <i className="bi bi-lightning-charge-fill me-1"></i> 3. Bonus por Tiempo
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button 
                                            type="button" 
                                            className={`nav-link fw-bold ${activeTab === 'screen' ? 'active bg-warning text-dark shadow-sm' : 'text-secondary'}`}
                                            onClick={() => setActiveTab('screen')}
                                        >
                                            <i className="bi bi-display-fill me-1"></i> 4. Pantalla Estadio & OBS
                                        </button>
                                    </li>
                                </ul>

                                {/* PESTAÑA 1: GENERAL & PATROCINIO */}
                                {activeTab === 'general' && (
                                    <div className="p-2">
                                        <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">
                                            Información General y Patrocinios Principales
                                        </h6>
                                        <div className="row g-3">
                                            <div className="col-12 mb-2">
                                                <label className="form-label fw-bold">Nombre del Tema / Preset:</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    value={formData.themeName} 
                                                    onChange={e => setFormData({ ...formData, themeName: e.target.value })} 
                                                    placeholder="Ej. Pégale al Diablo - Corona 2026" 
                                                    required 
                                                />
                                                <small className="text-muted">Nombre identificador con el que aparecerá en la consola del operador y salas.</small>
                                            </div>

                                            <div className="col-md-6">
                                                <FileDropzone 
                                                    label="Logo del Patrocinador (Móvil y Portada):" 
                                                    value={formData.sponsorLogo} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="logo" 
                                                    fieldName="sponsorLogo" 
                                                    uploadingField={uploadingField}
                                                    helpText="Logo PNG transparente sugerido para la interfaz del aficionado."
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <FileDropzone 
                                                    label="Patrocinador Pantalla Estadio:" 
                                                    value={formData.screenSponsor} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="logo" 
                                                    fieldName="screenSponsor" 
                                                    uploadingField={uploadingField}
                                                    helpText="Logo o cintillo del patrocinador proyectado en pantalla gigante."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* PESTAÑA 2: MÓVIL AFICIONADO & HOYOS */}
                                {activeTab === 'fan' && (
                                    <div className="p-2">
                                        <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">
                                            Elementos de la Pantalla del Aficionado y Hoyos (Tap Gameplay)
                                        </h6>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <FileDropzone 
                                                    label="Fondo Celular Aficionado:" 
                                                    value={formData.fanBackground} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="bg" 
                                                    fieldName="fanBackground" 
                                                    uploadingField={uploadingField}
                                                    helpText="Fondo vertical 9:16 para la pantalla del celular del aficionado."
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <FileDropzone 
                                                    label="Personaje Normal (Espera):" 
                                                    value={formData.characterNormal} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="character" 
                                                    fieldName="characterNormal" 
                                                    uploadingField={uploadingField}
                                                    helpText="Sprite del personaje asomándose (PNG transparente)."
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <FileDropzone 
                                                    label="Personaje Golpeado (Impacto):" 
                                                    value={formData.characterHit} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="character" 
                                                    fieldName="characterHit" 
                                                    uploadingField={uploadingField}
                                                    helpText="Sprite del personaje al recibir el golpe/tap (PNG transparente)."
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <FileDropzone 
                                                    label="Obstáculo Trasero (Fondo del Hoyo):" 
                                                    value={formData.backObstacle} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="element" 
                                                    fieldName="backObstacle" 
                                                    uploadingField={uploadingField}
                                                    helpText="Capa trasera del hoyo o madriguera (detrás del personaje)."
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <FileDropzone 
                                                    label="Obstáculo Delantero (Frente del Hoyo):" 
                                                    value={formData.frontObstacle} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="element" 
                                                    fieldName="frontObstacle" 
                                                    uploadingField={uploadingField}
                                                    helpText="Capa delantera del hoyo o césped (oculta el tronco del personaje)."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* PESTAÑA 3: BONUS POR TIEMPO */}
                                {activeTab === 'bonus' && (
                                    <div className="p-2">
                                        <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">
                                            Gráficos e Indicadores de Bonus por Tiempo (Etapas 1, 2 y 3)
                                        </h6>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <FileDropzone 
                                                    label="Bonus Etapa 1 (Inicio):" 
                                                    value={formData.bonusStage1} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="bonus" 
                                                    fieldName="bonusStage1" 
                                                    uploadingField={uploadingField}
                                                    helpText="Elemento o multiplicador x1 activo durante el primer tercio."
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <FileDropzone 
                                                    label="Bonus Etapa 2 (Intermedio):" 
                                                    value={formData.bonusStage2} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="bonus" 
                                                    fieldName="bonusStage2" 
                                                    uploadingField={uploadingField}
                                                    helpText="Elemento o multiplicador x2 activo a mitad del tiempo."
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <FileDropzone 
                                                    label="Bonus Etapa 3 (Frenesí):" 
                                                    value={formData.bonusStage3} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="bonus" 
                                                    fieldName="bonusStage3" 
                                                    uploadingField={uploadingField}
                                                    helpText="Elemento o multiplicador x3 en los últimos segundos."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* PESTAÑA 4: PANTALLA ESTADIO & OBS OVERLAY */}
                                {activeTab === 'screen' && (
                                    <div className="p-2">
                                        <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">
                                            Gráficos para Pantalla Estadio y Banner Overlay (OBS Studio)
                                        </h6>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <FileDropzone 
                                                    label="Fondo Pantalla Estadio (16:9):" 
                                                    value={formData.screenBackground} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="bg" 
                                                    fieldName="screenBackground" 
                                                    uploadingField={uploadingField}
                                                    helpText="Fondo en alta resolución (1920x1080) para pantalla gigante."
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <FileDropzone 
                                                    label="Banner Overlay Transmisión (1536px):" 
                                                    value={formData.overlayBanner1536} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="overlay" 
                                                    fieldName="overlayBanner1536" 
                                                    uploadingField={uploadingField}
                                                    helpText="Cintillo inferior horizontal para OBS (resolución estándar 1536px)."
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <FileDropzone 
                                                    label="Banner Overlay Transmisión (1920px):" 
                                                    value={formData.overlayBanner1920} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="overlay" 
                                                    fieldName="overlayBanner1920" 
                                                    uploadingField={uploadingField}
                                                    helpText="Cintillo inferior horizontal para OBS (Full HD 1920px)."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="modal-footer border-0 px-0 pb-0 pt-4 mt-3 border-top">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => setIsEditing(false)}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-warning fw-bold text-dark px-4 shadow-sm" 
                                        disabled={loading || !!uploadingField}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-lg me-1"></i> Guardar Tema
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export { RoosterThemeModal };
export default RoosterThemeModal;
